import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { CheckCircle2, TrendingUp, Users, Zap } from "lucide-react";

export const Route = createFileRoute("/revenda-iptv/quanto-ganha")({
  head: () => ({
    meta: [
      { title: "Quanto Ganha um Revendedor IPTV em 2026? Estudo de Lucro | P2 PRO" },
      { name: "description", content: "Descubra o potencial real de lucro da revenda IPTV. Analisamos margens, custos e projeções de faturamento para iniciantes e profissionais no modelo pós-pago." },
      { name: "keywords", content: "lucro revenda iptv, quanto ganha revendedor iptv, faturamento iptv, margem de lucro iptv, renda extra streaming" },
    ],
  }),
  component: () => (
    <SEOPage
      variant="centered"
      hideStats={false}
      showPriceTable={true}
      category="Financeiro & Lucratividade"
      title="QUANTO GANHA UM REVENDEDOR IPTV? ANÁLISE REAL DE LUCRO"
      subtitle="Desvendamos as margens e o potencial de faturamento do mercado que mais cresce no Brasil. Saiba como escalar seus ganhos."
      introParagraphs={[
        "A dúvida sobre o faturamento é, compreensivelmente, a mais frequente entre quem planeja ingressar neste mercado. A resposta curta é: seu lucro é diretamente proporcional à sua capacidade de prospecção e à estabilidade do produto que você entrega. No entanto, para entender a matemática real por trás desse negócio, precisamos analisar as margens líquidas e o custo de aquisição de clientes (CAC).",
        "Diferente de modelos de franquia tradicionais que exigem royalties ou taxas mensais, a <a href='/' className='text-electric hover:underline font-bold'>Revenda IPTV da P2 PRO</a> opera em um sistema de margem pura. No nosso modelo pós-pago, seu investimento inicial é quase nulo, pois você não precisa comprar grandes lotes de créditos antecipadamente. Isso significa que, desde a sua primeira venda, o dinheiro que entra no seu bolso é quase 100% lucro bruto, descontando apenas o custo unitário da ativação.",
        "Neste artigo, vamos detalhar quatro níveis de faturamento, baseados na média histórica de nossos parceiros em 2026. Mostraremos como é perfeitamente possível sair de uma renda extra modesta para um faturamento empresarial recorrente, utilizando as ferramentas certas e o suporte estratégico de nossa equipe."
      ]}
      sections={[
        {
          title: "Projeções de Faturamento Mensal",
          type: "grid",
          content: (
            <>
              {[
                { title: "Nível: Iniciante", desc: "Com 20 a 50 clientes ativos. Faturamento entre R$ 800,00 e R$ 2.000,00 mensais dedicando poucas horas por dia.", icon: <Zap className="text-yellow-400" /> },
                { title: "Nível: Profissional", desc: "Acima de 150 clientes ativos. Renda entre R$ 5.000,00 e R$ 8.000,00. Aqui o revendedor já vive exclusivamente do negócio.", icon: <TrendingUp className="text-green-400" /> },
                { title: "Nível: Distribuidor", desc: "Com rede de sub-revenda. Ganhos acima de R$ 15.000,00 mensais através da margem sobre ativações da própria rede.", icon: <Users className="text-blue-400" /> }
              ].map((item, i) => (
                <div key={i} className="p-10 rounded-[2.5rem] bg-white/[0.02] border border-white/5 text-center">
                  <div className="flex justify-center mb-6">{item.icon}</div>
                  <h3 className="text-xl font-black mb-4 italic uppercase">{item.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </>
          )
        },
        {
          title: "A Matemática do Modelo Pós-Pago",
          type: "featured",
          content: (
            <div className="text-white">
              <div className="max-w-3xl mx-auto space-y-8">
                <p className="text-lg leading-relaxed text-center italic">
                  "No modelo pós-pago da P2 PRO, sua margem de lucro por ativação gira em torno de 300% a 500%, dependendo do plano que você comercializa para o cliente final."
                </p>
                <div className="grid md:grid-cols-2 gap-8">
                  <div className="bg-black/30 p-8 rounded-2xl border border-white/10">
                    <h4 className="text-electric font-black mb-4 uppercase tracking-widest">Baixo Risco</h4>
                    <p className="text-sm text-white/60">Sem estoque parado, seu capital de giro fica livre. Se você não vender, não tem custo. Simples e justo.</p>
                  </div>
                  <div className="bg-black/30 p-8 rounded-2xl border border-white/10">
                    <h4 className="text-electric font-black mb-4 uppercase tracking-widest">Escala de Lucro</h4>
                    <p className="text-sm text-white/60">Conforme você aumenta seu volume de ativações mensais, o custo unitário do crédito cai, aumentando sua margem líquida.</p>
                  </div>
                </div>
              </div>
            </div>
          )
        }
      ]}
      benefits={[
        { title: "Renda 100% Recorrente", description: "O cliente paga todo mês para continuar assistindo. O trabalho de venda é feito apenas uma vez." },
        { title: "Margens de Elite", description: "Lucratividade muito superior a qualquer franquia física de alimentação ou serviços." },
        { title: "Recebimento Instantâneo", description: "O cliente paga diretamente no seu Pix. O dinheiro cai na hora na sua conta." }
      ]}
      howItWorks={[
        { title: "Venda", description: "Feche o plano mensal com o cliente (Ex: R$ 35,00)." },
        { title: "Ativação", description: "Custa apenas uma fração disso para você no painel." },
        { title: "Lucro", description: "A diferença (Ex: R$ 25,00) fica no seu bolso instantaneamente." },
        { title: "Renovação", description: "No mês seguinte, o lucro se repete com custo de aquisição zero." }
      ]}
      diferenciais={[
        { title: "Zero Taxa de Adesão", description: "Não cobramos para você entrar na nossa rede de parceiros de sucesso." },
        { title: "Scripts de Vendas", description: "Ensinamos você a valorizar seu produto para cobrar preços mais altos." },
        { title: "Retenção Absoluta", description: "P2PRO não trava. Produto de qualidade garante que o cliente nunca te abandone." }
      ]}
      faq={[
        { question: "É garantido que vou lucrar?", answer: "Sim, desde que você siga nossos treinamentos e utilize a ferramenta de testes para converter clientes." },
        { question: "Quanto tempo devo trabalhar?", answer: "Você é dono do seu tempo. Muitos lucram R$ 2.000,00 extras trabalhando apenas no período da noite." },
        { question: "A P2 PRO fica com alguma parte?", answer: "Não. Você é um revendedor independente. Sua única relação financeira conosco é a ativação dos créditos." }
      ]}
      breadcrumbs={[
        { label: "Revenda IPTV", href: "/revenda-iptv" },
        { label: "Quanto Ganha", href: "/revenda-iptv/quanto-ganha" }
      ]}
      relatedArticles={[
        { title: "Como Ser um Revendedor", href: "/revenda-iptv/como-ser" },
        { title: "Planos de Créditos", href: "/revenda-iptv/plano" },
        { title: "Revenda para Iniciantes", href: "/revenda-iptv/iniciantes" }
      ]}
    />
  ),
});
